/**
 * Store coverage — who else has worked a call this month (#3454).
 *
 * The schedule is per-associate, so a colleague covering a call lands on their
 * own route row and is invisible to the rep looking at theirs. The API now
 * reports the store's whole picture; these helpers turn it into what a rep
 * needs to know standing in a parking lot: has this already been done, and by
 * whom — without naming the rep back to themselves.
 */

export interface CoverageContributor {
  associate_id?: number | null;
  name: string;
  count: number;
  last?: string | null;
}

export interface CoverageLike {
  visits_completed_store?: number | null;
  visits_completed_by_others?: number | null;
  completed_by?: CoverageContributor[] | null;
}

/**
 * Contributors other than `myAssociateId`, keeping the API's most-recent-first
 * order and dropping anyone with nothing to their name.
 */
export function othersWhoCovered(
  completedBy: CoverageContributor[] | null | undefined,
  myAssociateId: number | null | undefined,
): CoverageContributor[] {
  return (completedBy ?? []).filter(
    (c) => c.count > 0 && (myAssociateId == null || c.associate_id !== myAssociateId),
  );
}

/**
 * Store-wide completions, never fewer than the rep's own.
 *
 * The two counts are scoped differently — the rep's own counts every visit on
 * their route, the store's counts visits DATED in the billing month — so a
 * visit dated outside the month is in one and not the other. Showing the
 * smaller number would tell a rep who completed a visit that they have done
 * none, and drag the progress bar backwards.
 */
export function storeVisitsFor(coverage: CoverageLike | null | undefined, mine: number): number {
  const store = coverage?.visits_completed_store;
  return store == null ? mine : Math.max(store, mine);
}

/**
 * One-line summary for a call another rep has already worked, or null when
 * there is nothing worth saying. Frequency-aware on purpose: "2 of 3 done"
 * still needs a visit, and collapsing that to "done" would cost a visit.
 */
export function coverageSummary(
  coverage: CoverageLike | null | undefined,
  myAssociateId: number | null | undefined,
  visitsRequired: number | null | undefined,
): string | null {
  const others = othersWhoCovered(coverage?.completed_by, myAssociateId);
  if (!others.length) return null;
  const names = others.map((c) => c.name).join(', ');
  const done = coverage?.visits_completed_store ?? 0;
  if (!visitsRequired) return `Also visited by ${names}`;
  return `${done} of ${visitsRequired} done — ${names}`;
}
